import { ButtonBase } from '@mui/material';
import { useApp } from 'contexts/app';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, IconButton, Paper, Stack, styled, Text, Tooltip, ViewMore } from 'shell/components';
import { getTooltipComponent } from 'utils/helper';

const FeedbackRenderer = ({
    feedback,
    selectedFeedbackId,
    setSelectedFeedbackId,
    onClickThumbsUp,
    onClickThumbsDown,
}) => {
    const { t } = useTranslation('assignment');
    if (feedback.is_failed) {
        return (
            <>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'} weight={'semibold'}>
                        {t('feedback-section.assignment-feedback-label', 'Assignment Feedback')}
                        {t('feedback-section.assignment-feedback-label', 'Assignment Feedback')}
                    </Text>
                </BackgroundStack>
                <Stack sx={{ p: 3 }}>
                    <Stack
                        sx={(theme) => ({
                            borderRadius: '8px',
                            p: theme.spacing(2, 3),
                            background: theme.palette.ensightful.warning[100],
                        })}
                        alignItems={'flex-start'}
                        spacing={2}
                    >
                        <Text color={(theme) => theme.palette.ensightful.warning[900]}>
                            {t(
                                'feedback-section.feedback-generation-failed-try-again',
                                'We were unable to generate feedback for this assignment. Please click the regenerate button above to try again.'
                            )}
                        </Text>
                    </Stack>
                </Stack>
            </>
        );
    }

    if (typeof feedback.feedback === 'string') {
        return (
            <>
                <BackgroundStack>
                    <Text variant={'p1'} weight={'semibold'}>
                        {t('feedback-section.assignment-feedback-label', 'Assignment Feedback')}
                    </Text>
                </BackgroundStack>
                <Stack sx={{ p: 3 }}>
                    <CustomText rich>{feedback.feedback}</CustomText>
                </Stack>
            </>
        );
    }

    if (feedback.feedback.feedback_type === 'rubric') {
        return (
            <>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'} weight={'semibold'}>
                        {t('feedback-section.assignment-feedback-label', 'Assignment Feedback')}
                    </Text>
                </BackgroundStack>
                <Stack sx={{ flex: 1, p: 3 }} spacing={2.5}>
                    {feedback?.feedback?.improvement_suggestions
                        .filter(({ active }) => active)
                        .map((item) => (
                            <CriterionFeedback
                                key={item.id}
                                feedback={item}
                                selectedFeedbackId={selectedFeedbackId}
                                setSelectedFeedbackId={setSelectedFeedbackId}
                                onClickThumbsUp={onClickThumbsUp}
                                onClickThumbsDown={onClickThumbsDown}
                            />
                        ))}
                </Stack>
            </>
        );
    }

    if (feedback.feedback.feedback_type === 'system') {
        return (
            <>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text variant={'p1'} weight={'semibold'}>
                            {t('feedback-section.general-feedback-label', 'General Feedback')}
                        </Text>
                        <Tooltip
                            content={getTooltipComponent(
                                t('feedback-section.general-feedback-label', 'General Feedback'),
                                t(
                                    'feedback-section.system-feedback-tooltip',
                                    'This section provides a broad overview, highlighting key strengths and areas for improvement across your work.'
                                )
                            )}
                            type={'info'}
                        />
                    </Stack>
                </BackgroundStack>
                <Stack sx={{ p: 3 }}>
                    <>
                        {!feedback?.feedback?.commentary ? (
                            <Text sx={{ my: 4 }} align={'center'} color={'light'}>
                                {t(
                                    'feedback-section.instructor-deleted-feedback',
                                    'This feedback was deleted by your instructor(s).'
                                )}
                            </Text>
                        ) : (
                            <CustomText rich>{feedback.feedback.commentary}</CustomText>
                        )}
                    </>
                </Stack>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text variant={'p1'} weight={'semibold'}>
                            {t('feedback-section.detailed-feedback-label', 'Detailed Feedback')}
                        </Text>
                        <Tooltip
                            content={getTooltipComponent(
                                t('feedback-section.detailed-feedback-label', 'Detailed Feedback'),
                                t(
                                    'feedback-section.detailed-feedback-tooltip',
                                    'Here, specific feedback is provided with highlighted examples. You can use these examples to help them identify other areas where you can improve.'
                                )
                            )}
                            type={'info'}
                        />
                    </Stack>
                </BackgroundStack>
                <Stack sx={{ position: 'relative', flex: 1, p: 3 }} spacing={2.5}>
                    {feedback?.feedback?.improvement_suggestions.every(({ active }) => !active) ? (
                        <Text
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '100%',
                            }}
                            align={'center'}
                            color={'light'}
                        >
                            {t(
                                'feedback-section.instructor-deleted-feedback',
                                'This feedback was deleted by your instructor(s).'
                            )}
                        </Text>
                    ) : (
                        feedback?.feedback?.improvement_suggestions
                            .filter(({ active }) => active)
                            .map((item) => (
                                <DetailedFeedback
                                    key={item.id}
                                    feedback={item}
                                    selectedFeedbackId={selectedFeedbackId}
                                    setSelectedFeedbackId={setSelectedFeedbackId}
                                    onClickThumbsUp={onClickThumbsUp}
                                    onClickThumbsDown={onClickThumbsDown}
                                />
                            ))
                    )}
                </Stack>
            </>
        );
    }

    if (feedback.feedback.feedback_type === 'custom') {
        return (
            <>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text variant={'p1'} weight={'semibold'}>
                            {t('feedback-section.general-feedback-label', 'General Feedback')}
                        </Text>
                    </Stack>
                </BackgroundStack>
                <Stack sx={{ p: 3 }} alignItems={'flex-start'} spacing={2}>
                    <Text rich>{feedback.feedback.commentary}</Text>
                </Stack>
            </>
        );
    }

    return null;
};

const CriterionFeedback = ({
    feedback,
    selectedFeedbackId,
    setSelectedFeedbackId,
    onClickThumbsUp,
    onClickThumbsDown,
}) => {
    const { currentAssignment } = useApp();
    const { t } = useTranslation('assignment');

    const [isHovered, setIsHovered] = useState(false);

    return (
        <CustomButtonBase
            component={'div'}
            role={'presentation'}
            onClick={() => setSelectedFeedbackId(feedback.id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
                <Stack spacing={2}>
                    <Text variant={'sh2'} weight={'semibold'}>
                        {currentAssignment.rubric.criteria.find(({ id }) => id === feedback.criterion_id).title}
                    </Text>
                    <Divider />
                    <Stack spacing={3}>
                        <Stack spacing={2}>
                            <Text variant={'p1'}>{t('feedback-section.positive-title', 'What you did well')}</Text>
                            {feedback.positive.active ? (
                                <ViewMore rich text={feedback.positive.text} />
                            ) : (
                                <Text color={'light'}>
                                    {t('feedback-section.deleted-feedback', 'This feedback was deleted.')}
                                </Text>
                            )}
                        </Stack>
                        <Stack
                            sx={(theme) => ({
                                borderRadius: '4px',
                                mx: -1,
                                p: 2,
                                background: theme.palette.ensightful.warning[200],
                            })}
                            spacing={2}
                        >
                            <Text variant={'p1'}>How you can improve</Text>
                            {feedback.negative.active ? (
                                <ViewMore rich text={feedback.negative.text} />
                            ) : (
                                <Text color={'light'}>
                                    {t('feedback-section.deleted-feedback', 'This feedback was deleted.')}
                                </Text>
                            )}
                        </Stack>
                    </Stack>
                    {onClickThumbsUp && onClickThumbsDown && (
                        <Stack sx={{ alignSelf: 'flex-end' }} direction={'row'} spacing={2}>
                            <AgreeIconButton
                                aria-label={'agree'}
                                active={feedback.agree}
                                kind={'thumbsUp'}
                                size={'small'}
                                tooltip={{ component: t('feedback-section.this-was-helpful', 'This was helpful') }}
                                variant={'square'}
                                onClick={() => onClickThumbsUp(feedback)}
                            />
                            <DisagreeIconButton
                                aria-label={'disagree'}
                                active={feedback.disagree}
                                kind={'thumbsDown'}
                                size={'small'}
                                tooltip={{
                                    component: t(
                                        'feedback-section.this-was-not-helpful',
                                        'This was unhelpful or incorrect'
                                    ),
                                }}
                                variant={'square'}
                                onClick={() => onClickThumbsDown(feedback)}
                            />
                        </Stack>
                    )}
                </Stack>
                {selectedFeedbackId !== feedback.id && isHovered && <LeftBorderHighlight />}
            </CustomPaper>
        </CustomButtonBase>
    );
};

const DetailedFeedback = ({
    feedback,
    selectedFeedbackId,
    setSelectedFeedbackId,
    onClickThumbsUp,
    onClickThumbsDown,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const { t } = useTranslation('assignment');

    return (
        <CustomButtonBase
            component={'div'}
            role={'presentation'}
            onClick={() => setSelectedFeedbackId(feedback.id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
                <Stack spacing={2}>
                    <Text align={'left'} rich>
                        {feedback.text}
                    </Text>
                    {onClickThumbsUp && onClickThumbsDown && (
                        <Stack sx={{ alignSelf: 'flex-end' }} direction={'row'} spacing={2}>
                            <AgreeIconButton
                                aria-label={'agree'}
                                active={feedback.agree}
                                kind={'thumbsUp'}
                                size={'small'}
                                tooltip={{ component: t('feedback-section.this-was-helpful', 'This was helpful') }}
                                variant={'square'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClickThumbsUp(feedback);
                                }}
                            />
                            <DisagreeIconButton
                                aria-label={'disagree'}
                                active={feedback.disagree}
                                kind={'thumbsDown'}
                                size={'small'}
                                tooltip={{
                                    component: t(
                                        'feedback-section.this-was-not-helpful',
                                        'This was unhelpful or incorrect'
                                    ),
                                }}
                                variant={'square'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClickThumbsDown(feedback);
                                }}
                            />
                        </Stack>
                    )}
                </Stack>
                {selectedFeedbackId !== feedback.id && isHovered && <LeftBorderHighlight />}
            </CustomPaper>
        </CustomButtonBase>
    );
};

const CustomText = styled(Text)(() => {
    return {
        '& ul li': {
            listStyleType: '"- "',
        },
    };
});

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 5),
        background: theme.palette.ensightful.primary[50],
    };
});

const CustomButtonBase = styled(ButtonBase)(({ theme }) => {
    return {
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomPaper = styled(Paper)(({ theme, isActive }) => {
    return {
        position: 'relative',
        width: '100%',
        padding: theme.spacing(3),
        ...(isActive
            ? {
                  border: `2px solid ${theme.palette.ensightful.warning[700]}`,
              }
            : {
                  border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                  padding: `calc(${theme.spacing(3)} + 1px)`,
              }),
    };
});

const AgreeIconButton = styled(IconButton)(({ theme, active }) => {
    return {
        ...(active && {
            background: theme.palette.ensightful.success[100],
            color: theme.palette.ensightful.success[700],
            '&:hover': {
                background: theme.palette.ensightful.success[100],
                color: theme.palette.ensightful.success[700],
            },
        }),
    };
});

const DisagreeIconButton = styled(IconButton)(({ theme, active }) => {
    return {
        ...(active && {
            background: theme.palette.ensightful.error[100],
            color: theme.palette.ensightful.error[700],
            '&:hover': {
                background: theme.palette.ensightful.error[100],
                color: theme.palette.ensightful.error[700],
            },
        }),
    };
});

const LeftBorderHighlight = styled(Box)(({ theme }) => {
    return {
        position: 'absolute',
        top: '50%',
        left: '-1px',
        height: 'calc(100% - 12px)',
        width: '2px',
        background: theme.palette.ensightful.warning[700],
        transform: 'translateY(-50%)',
    };
});

export default FeedbackRenderer;
